import { logger } from '@fiverr-private/obs';
import {
    type SellerPageFederatedSeller,
    type GigData,
    type ReviewsData,
    getCategoriesText,
} from '@fiverr-private/seller_pages_toolbox';

const SCHEMA_CONTEXT = 'https://schema.org';
const FIVERR_URL = 'https://www.fiverr.com';
const ORGANIZATION_ID = `${FIVERR_URL}/#organization`;
// url + logo must match the site-wide Organization schema so Google merges both into one entity.
const FIVERR_HOMEPAGE_URL = 'https://www.fiverr.com/';
const FIVERR_LOGO_URL =
    'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto,h_224/v1/attachments/generic_asset/asset/d60fda24bdfb2d58a9f9fcdd9b2e019e-1668776270346/Fiverr_Logo_GreenGreen_RGB%20%281%29.png';

const MAX_KNOWS_ABOUT = 10;
const MAX_DESCRIPTION_LENGTH = 500;
const MIN_REVIEWS_FOR_RATING = 5;
const MIN_RATING = 1;
const MAX_RATING = 5;

type JsonLdObject = Record<string, unknown>;

const toIsoDate = (unixSeconds?: number | null): string | undefined => {
    if (!unixSeconds) {
        return undefined;
    }
    const date = new Date(unixSeconds * 1000);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
};

const toAbsoluteUrl = (url: string, origin: string): string => {
    try {
        return new URL(url, origin).toString();
    } catch {
        return url;
    }
};

const getOrigin = (url: string): string => {
    try {
        return new URL(url).origin;
    } catch {
        return FIVERR_URL;
    }
};

const cleanDescription = (description?: string | null): string | undefined => {
    if (!description) {
        return undefined;
    }
    const text = description
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    return text ? text.slice(0, MAX_DESCRIPTION_LENGTH) : undefined;
};

const buildHasCredential = (seller: SellerPageFederatedSeller): JsonLdObject | undefined => {
    if (seller.isPro !== true) {
        return undefined;
    }

    const credential: JsonLdObject = {
        '@type': 'EducationalOccupationalCredential',
        name: 'Fiverr Pro Vetted',
        credentialCategory: 'Professional Certification',
        recognizedBy: {
            '@type': 'Organization',
            name: 'Fiverr',
            url: FIVERR_URL,
        },
    };

    try {
        // Spec: competencyRequired is a single vetted category string. proSubCategories is a list,
        // so we take the first resolved category.
        const [competencyRequired] = getCategoriesText(seller.proSubCategories || []).filter(Boolean) as string[];
        if (competencyRequired) {
            credential.competencyRequired = competencyRequired;
        }
    } catch (originalError) {
        logger.warn('Failed to resolve competencyRequired for seller schema', {
            cause: originalError instanceof Error ? originalError.message : originalError,
        });
    }

    return credential;
};

// reviewCount must be the exact integer from the backend, never the rounded UI string.
const buildAggregateRating = (
    seller: SellerPageFederatedSeller,
    reviewsData?: ReviewsData
): JsonLdObject | undefined => {
    const reviewCount = reviewsData?.buying_reviews?.total_count;
    const ratingValue = seller.rating?.score;

    const hasEnoughReviews = typeof reviewCount === 'number' && reviewCount >= MIN_REVIEWS_FOR_RATING;
    const hasValidRating = typeof ratingValue === 'number' && ratingValue >= MIN_RATING && ratingValue <= MAX_RATING;

    if (!hasEnoughReviews || !hasValidRating) {
        return undefined;
    }

    const aggregateRating: JsonLdObject = {
        '@type': 'AggregateRating',
        ratingValue: String(ratingValue),
        reviewCount,
        bestRating: String(MAX_RATING),
        worstRating: String(MIN_RATING),
    };

    if (typeof seller.rating?.count === 'number') {
        aggregateRating.ratingCount = seller.rating.count;
    }

    return aggregateRating;
};

const buildKnowsAbout = (seller: SellerPageFederatedSeller): string[] => {
    const skills = (seller.activeStructuredSkills || []).map((skill) => skill.name).filter(Boolean);

    let categories: string[] = [];
    try {
        categories = getCategoriesText(seller.gigCategories || []).filter(Boolean) as string[];
    } catch (originalError) {
        logger.warn('Failed to resolve gig categories for seller schema knowsAbout', {
            cause: originalError instanceof Error ? originalError.message : originalError,
        });
    }

    return [...new Set([...skills, ...categories])].slice(0, MAX_KNOWS_ABOUT);
};

const buildHasOfferCatalog = (gigs: GigData[], origin: string): JsonLdObject | undefined => {
    const itemListElement = gigs
        .filter((gig) => Boolean(gig?.title && gig?.gig_url))
        .map((gig) => {
            const service: JsonLdObject = {
                '@type': 'Service',
                name: gig.title,
                url: toAbsoluteUrl(gig.gig_url, origin),
                areaServed: 'Worldwide',
            };
            return {
                '@type': 'Offer',
                itemOffered: service,
            };
        });

    if (!itemListElement.length) {
        return undefined;
    }

    return {
        '@type': 'OfferCatalog',
        name: 'Services Offered',
        itemListElement,
    };
};

export interface BuildProfilePageArgs {
    seller: SellerPageFederatedSeller;
    reviewsData?: ReviewsData;
    gigs?: GigData[];
    canonicalUrl: string;
}

/** Block A — ProfilePage wrapping the seller Person entity. */
export const buildProfilePageJsonLd = ({
    seller,
    reviewsData,
    gigs = [],
    canonicalUrl,
}: BuildProfilePageArgs): JsonLdObject => {
    const { user } = seller;

    const person: JsonLdObject = {
        '@type': 'Person',
        name: user.profile?.displayName || user.name,
        url: canonicalUrl,
        memberOf: { '@id': ORGANIZATION_ID },
    };

    if (user.profileImageUrl) {
        person.image = user.profileImageUrl;
    }

    const jobTitle = seller.oneLinerTitle?.trim();
    if (jobTitle) {
        person.jobTitle = jobTitle;
    }

    const description = cleanDescription(seller.description);
    if (description) {
        person.description = description;
    }

    const knowsAbout = buildKnowsAbout(seller);
    if (knowsAbout.length) {
        person.knowsAbout = knowsAbout;
    }

    const hasCredential = buildHasCredential(seller);
    if (hasCredential) {
        person.hasCredential = hasCredential;
    }

    const aggregateRating = buildAggregateRating(seller, reviewsData);
    if (aggregateRating) {
        person.aggregateRating = aggregateRating;
    }

    const hasOfferCatalog = buildHasOfferCatalog(gigs, getOrigin(canonicalUrl));
    if (hasOfferCatalog) {
        person.hasOfferCatalog = hasOfferCatalog;
    }

    const profilePage: JsonLdObject = {
        '@context': SCHEMA_CONTEXT,
        '@type': 'ProfilePage',
        url: canonicalUrl,
        mainEntity: person,
    };

    const dateCreated = toIsoDate(user.joinedAt);
    if (dateCreated) {
        profilePage.dateCreated = dateCreated;
    }

    const inLanguage = (user.languages || []).map((language) => language.code?.toLowerCase()).filter(Boolean);
    if (inLanguage.length) {
        profilePage.inLanguage = inLanguage;
    }

    return profilePage;
};

/** Block B — static Organization entity anchor, identical on every seller page. */
export const buildOrganizationJsonLd = (): JsonLdObject => ({
    '@context': SCHEMA_CONTEXT,
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Fiverr',
    url: FIVERR_HOMEPAGE_URL,
    logo: FIVERR_LOGO_URL,
    sameAs: [
        'https://en.wikipedia.org/wiki/Fiverr',
        'https://www.wikidata.org/wiki/Q5410132',
        'https://www.linkedin.com/company/fiverr-com/',
        'https://www.instagram.com/fiverr/',
        'https://x.com/fiverr',
        'https://www.youtube.com/channel/UCQieDTrc3ZeCPPNoJEFbqaQ',
        'https://www.facebook.com/Fiverr',
    ],
});
